import { useEffect, useEffectEvent, useMemo, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@components/ui/Button/Button';
import { Field } from '@components/ui/Field/Field';
import { SegmentedControl } from '@components/ui/SegmentedControl/SegmentedControl';
import { MfaCodeInput } from '@components/Mfa/MfaCodeInput/MfaCodeInput';
import {
  challengeMethodsToOffer,
  MfaInvalidCodeError,
  MfaRateLimitedError,
  sendMfaOtp,
  verifyMfa,
  type IMfaSession,
} from '@apis/mfa';
import type { MfaChallengeMethod, MfaOtpChannel, MfaVerifyMethod } from '@interfaces/IMfa';
import { MFA_CODE_LENGTH, MFA_OTP_RESEND_COOLDOWN_SECONDS, MFA_PENDING_TTL_MS } from '@constants/mfa';
import { useResendCooldown } from '@hooks/useResendCooldown';
import styles from './styles.module.css';

interface IMfaChallengeProps {
  /** The factors the login response allowed (`{mfaRequired: true, methods}`). */
  methods: MfaChallengeMethod[];
  /**
   * One-time-code channels the code may be sent on, and which is the default.
   *
   * Both optional: a backend that predates the SMS-channel amendment sends neither, and a user
   * with a single channel needs no choice. The picker appears only when there is a real one.
   */
  otpChannels?: MfaOtpChannel[];
  primaryOtpChannel?: MfaOtpChannel;
  /** `mfa/verify` returned `{user}` — the session is real, continue post-login. */
  onVerified: (session: IMfaSession) => void | Promise<void>;
  /**
   * `dorca_pending` has aged out. The caller drops back to the password form
   * with a "please sign in again" notice.
   */
  onPendingExpired: () => void;
  /** Abandon the challenge (e.g. wrong account) and return to the password form. */
  onCancel?: () => void;
}

/**
 * Second-factor challenge. Rendered as an in-flow step of the sign-in card, so
 * it owns no page chrome — just the heading, the factor switcher, and the code
 * field.
 *
 * Two security properties are load-bearing here:
 *  1. **Neutral failure copy.** `mfa/verify` answers a bare 401 for a wrong
 *     code, a replayed backup code, an exhausted OTP and a dead pending state
 *     alike. The UI says one thing for all of them; it must never hint that the
 *     code was merely *late*, which would confirm the code itself was right.
 *  2. **Expiry is time-driven, not error-driven.** Because of (1), the only
 *     honest way to detect a dead pending state is the clock: a timer armed for
 *     the cookie's TTL sends the user back to sign in.
 */
export function MfaChallenge({
  methods,
  otpChannels,
  primaryOtpChannel,
  onVerified,
  onPendingExpired,
  onCancel,
}: IMfaChallengeProps) {
  const { t } = useTranslation();
  const offered = useMemo(() => challengeMethodsToOffer(methods), [methods]);
  // An email-only account has no authenticator to fall back on: the only
  // push-able factor is the email OTP, so the challenge opens on it and requests
  // the first code immediately (below) rather than making the user hunt for a
  // "send" button. Guarded on `otp` being genuinely offered so a degenerate
  // empty `methods` never fires a send for a factor that isn't there.
  const emailOnly = !methods.includes('totp') && methods.includes('otp');
  // TOTP is the default whenever it's on offer (no round-trip to get a code);
  // otherwise fall back to whatever the pending state does allow (email OTP for
  // an email-only account).
  const [method, setMethod] = useState<MfaVerifyMethod>(() => offered[0] ?? 'totp');
  const [code, setCode] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const { cooldown, startCooldown, clearCooldown } = useResendCooldown();
  const channels = useMemo(() => otpChannels ?? [], [otpChannels]);
  // Which channel the NEXT send goes to. Starts at the user's default; the picker only exists
  // when they have more than one, so a single-channel account never sees a decision it cannot make.
  const firstChannel: MfaOtpChannel | undefined = channels[0];
  const [channel, setChannel] = useState<MfaOtpChannel | undefined>(
    () => primaryOtpChannel ?? channels[0],
  );

  const expire = useEffectEvent(() => onPendingExpired());
  useEffect(() => {
    const timer = window.setTimeout(expire, MFA_PENDING_TTL_MS);
    return () => window.clearTimeout(timer);
  }, []); // armed once, for the life of the pending cookie

  // Email-only challenge: fire the first OTP send on mount so the code is on its
  // way before the user even looks at the field. Ref-guarded so it happens
  // exactly once — StrictMode's double-invoke (dev) must not send twice.
  const autoSentRef = useRef(false);
  const autoSendOtp = useEffectEvent(() => {
    if (autoSentRef.current || !emailOnly) return;
    autoSentRef.current = true;
    void onSendOtp();
  });
  useEffect(() => {
    autoSendOtp();
  }, []); // once on mount; the guard inside decides whether a send is warranted

  function switchMethod(next: MfaVerifyMethod) {
    setMethod(next);
    setCode('');
    setErr(null);
  }

  /**
   * Switch delivery channel. Deliberately does NOT auto-send: an SMS costs money and the daily
   * budget is per user, so a user flicking between options would burn it without meaning to.
   * They pick, then press send.
   */
  function switchChannel(next: MfaOtpChannel) {
    if (next === channel) return;
    setChannel(next);
    setOtpSent(false);
    // Clear the cooldown too. It exists to absorb double-taps on ONE channel; leaving it armed
    // here would make the user wait out a minute in precisely the situation this picker exists
    // for — the first code never arrived and they want to try the other channel. Cost stays
    // bounded by the BFF's per-account brake and the engine's daily SMS budget, neither of which
    // this button can talk its way past.
    clearCooldown();
    setErr(null);
  }

  async function submit(rawCode: string) {
    const value = rawCode.trim();
    if (!value || submitting) return;
    setErr(null);
    setSubmitting(true);
    try {
      const session = await verifyMfa(method, value);
      await onVerified(session);
    } catch (error) {
      // One message for every 401 — wrong, replayed, exhausted, expired. Do NOT
      // branch this copy; see the component doc. 429 is the documented
      // exception: it says nothing about the code, and pretending the code was
      // wrong while every attempt is being refused just burns more attempts.
      setErr(
        error instanceof MfaInvalidCodeError
          ? t('auth.mfa.challenge.invalidCode')
          : error instanceof MfaRateLimitedError
            ? t('auth.mfa.rateLimited')
            : t('auth.mfa.challenge.genericError'),
      );
      setCode('');
    } finally {
      setSubmitting(false);
    }
  }

  async function onSendOtp() {
    if (cooldown > 0 || sendingOtp) return;
    setErr(null);
    setSendingOtp(true);
    try {
      await sendMfaOtp(channel);
      setOtpSent(true);
      startCooldown(MFA_OTP_RESEND_COOLDOWN_SECONDS);
    } catch (error) {
      setErr(
        error instanceof MfaRateLimitedError
          ? t('auth.mfa.rateLimited')
          : t('auth.mfa.challenge.sendOtpError'),
      );
    } finally {
      setSendingOtp(false);
    }
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    void submit(code);
  }

  const isBackup = method === 'backup';
  const canSubmit = isBackup ? code.trim().length > 0 : code.length === MFA_CODE_LENGTH;

  return (
    <div className={styles.root}>
      <div className={styles.heading}>
        <span className={styles.eyebrow}>{t('auth.mfa.eyebrow')}</span>
        <h1 className={styles.title}>{t('auth.mfa.challenge.title')}</h1>
        {/*
          Name the channel the code actually went to. The generic "we'll send you a code" is not
          wrong, but the screen knows the answer, and a user staring at the wrong inbox is the
          exact failure this feature was supposed to remove.
        */}
        <p className={styles.subtitle}>
          {method === 'otp' && channel
            ? t(`auth.mfa.challenge.subtitleChannel.${channel}`)
            : t(`auth.mfa.challenge.subtitle.${method}`)}
        </p>
      </div>

      {offered.length > 1 ? (
        <div className={styles.switcher}>
          <SegmentedControl<MfaVerifyMethod>
            value={method}
            onChange={switchMethod}
            ariaLabel={t('auth.mfa.challenge.methodSwitcherLabel')}
            options={offered.map((m) => ({ id: m, label: t(`auth.mfa.method.${m}`) }))}
          />
        </div>
      ) : null}

      <form onSubmit={onSubmit} className={styles.form} noValidate>
        {method === 'otp' && channels.length > 1 && firstChannel ? (
          <div className={styles.switcher}>
            <SegmentedControl<MfaOtpChannel>
              value={channel ?? firstChannel}
              onChange={switchChannel}
              ariaLabel={t('auth.mfa.challenge.channelSwitcherLabel')}
              options={channels.map((c) => ({ id: c, label: t(`auth.mfa.channel.${c}`) }))}
            />
          </div>
        ) : null}

        {method === 'otp' ? (
          <div className={styles.otpRow}>
            <Button variant="secondary" size="md" onClick={() => void onSendOtp()} disabled={sendingOtp || cooldown > 0}>
              {cooldown > 0
                ? t('auth.mfa.challenge.resendIn', { seconds: cooldown })
                : otpSent
                  ? t('auth.mfa.challenge.resendOtp')
                  : t('auth.mfa.challenge.sendOtp')}
            </Button>
            {otpSent ? (
              <span className={styles.sentNote} role="status">
                {channel ? t(`auth.mfa.challenge.otpSentTo.${channel}`) : t('auth.mfa.challenge.otpSent')}
              </span>
            ) : null}
          </div>
        ) : null}

        {isBackup ? (
          <Field
            label={t('auth.mfa.challenge.backupCodeLabel')}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            hint={t('auth.mfa.challenge.backupCodeHint')}
            autoComplete="off"
            spellCheck={false}
            autoFocus
            required
          />
        ) : (
          <MfaCodeInput
            label={t('auth.mfa.challenge.codeLabel')}
            value={code}
            onChange={setCode}
            // Six digits in means there is nothing left to decide — submit.
            onComplete={(full) => void submit(full)}
            disabled={submitting}
            autoFocus
          />
        )}

        {err ? (
          <div className={styles.error} role="alert">
            {err}
          </div>
        ) : null}

        <Button type="submit" variant="primary" size="md" block disabled={submitting || !canSubmit}>
          {submitting ? t('auth.mfa.challenge.verifying') : t('auth.mfa.challenge.submit')}
        </Button>
      </form>

      {onCancel ? (
        <div className={styles.footerRow}>
          <button type="button" className={styles.linkButton} onClick={onCancel}>
            {t('auth.mfa.challenge.backToLogin')}
          </button>
        </div>
      ) : null}
    </div>
  );
}
